<template>
    <div>
        <q-input
            @click="disable || $q.screen.gt.sm ? () => {} : (showPicker = true)"
            v-model="selectedDateTime"
            @keydown="preventInvalidTimeInput"
            :label="label"
            :dense="dense"
            :error="errorMessage !== ''"
            :error-message="errorMessage"
            :rules="[isValidDateTime, ...rules]"
            :disable="disable"
            :readonly="$q.screen.lt.sm ? true : false"
            class="datetime-picker-input"
            outlined
            :style="`padding-bottom: ${$q.screen.lt.md ? '0px' : '20px'}`"
            bg-color="white"
            color="accent"
            mask="####/##/## ##:##:##"
        >
            <!-- Date picker -->
            <template v-if="!noIcon" v-slot:prepend>
                <q-icon
                    @click="
                        () => {
                            showPicker = true;
                            activePicker = 'date';
                        }
                    "
                    name="event"
                    class="cursor-pointer"
                ></q-icon>
            </template>

            <!-- Time picker -->
            <template v-if="!noIcon" v-slot:append>
                <q-icon
                    @click="
                        () => {
                            showPicker = true;
                            activePicker = 'time';
                        }
                    "
                    name="access_time"
                    class="cursor-pointer"
                ></q-icon>
            </template>
        </q-input>

        <!-- Datetime picker dialog -->
        <q-dialog v-model="showPicker" @beforeHide="handleHideEvent">
            <q-date
                v-if="activePicker === 'date'"
                v-model="selectedDate"
                :mask="pickerMasks[userLocale]"
                :options="selectableDateRange"
                class="datetime-picker-dialog"
            >
                <div class="row">
                    <div align="left col">
                        <q-btn
                            @click="activePicker = 'time'"
                            class="q-px-none q-mx-none time-button"
                            icon="far fa-clock"
                            color="accent"
                            size="sm"
                            round
                        ></q-btn>
                    </div>
                    <div align="right" class="col">
                        <q-btn
                            @click="cancel"
                            :label="$t('Cancel')"
                            class="q-mr-sm"
                            color="white"
                            text-color="primary"
                            outline
                        />
                        <q-btn
                            @click="select"
                            :label="$t('Select')"
                            color="primary"
                            :disable="!selectedDate"
                        />
                    </div>
                </div>
            </q-date>
            <q-card v-else>
                <time-picker
                    v-model="selectedTime"
                    :with-seconds="!reportMode"
                    :minuteInterval="
                        reportMode
                            ? String(reportModeMinuteInterval)
                            : String(1)
                    "
                ></time-picker>
                <div class="row q-ma-md">
                    <div align="left col" class="q-pr-sm q-pt-xs">
                        <q-btn
                            @click="activePicker = 'date'"
                            class="q-px-none q-mx-none time-button"
                            icon="far fa-calendar-alt"
                            color="accent"
                            size="sm"
                            round
                        ></q-btn>
                    </div>
                    <div align="right" class="col">
                        <q-btn
                            @click="cancel"
                            :label="$t('Cancel')"
                            class="q-mr-sm"
                            color="white"
                            text-color="primary"
                            outline
                        />
                        <q-btn
                            @click="select"
                            :label="$t('Select')"
                            color="primary"
                        />
                    </div>
                </div>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import { LOCALE_DATETIME_FORMAT } from '@models/timeformats.js';
import { date } from 'quasar';
import TimePicker from './TimePicker.vue';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'DatetimePicker',
    components: {
        TimePicker
    },
    emits: ['update:modelValue'],
    props: {
        dense: {
            type: Boolean,
            required: false,
            default() {
                return false;
            }
        },
        noIcon: {
            type: Boolean,
            required: false,
            default() {
                return false;
            }
        },
        errorMessage: {
            type: String,
            required: false,
            default() {
                return '';
            }
        },
        rules: {
            type: Array,
            required: false,
            default() {
                return [];
            }
        },
        label: {
            type: String,
            required: false,
            default() {
                return '';
            }
        },
        modelValue: {
            type: String,
            required: false
        },
        disable: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * Limits the options to the given date range.
         * Expected value is {start, end} where start and end are date strings.
         */
        dateRange: {
            required: false,
            type: Object,
            default() {
                return {
                    start: null,
                    end: null
                };
            }
        },
        reportMode: {
            type: Boolean,
            required: false,
            default: false
        },
        readonly: {
            type: Boolean,
            required: false,
            default: false
        },
        enableValidationRules: {
            type: Boolean,
            required: false,
            default: true
        },
        allowEmpty: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * When true, automatically sets the current date/time when the picker is opened
         * and no value is currently selected. Does not override existing values.
         */
        defaultToNow: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            showPicker: false,
            activePicker: 'date', // valid values: date, time
            // datetime picker masks
            pickerMasks: LOCALE_DATETIME_FORMAT,
            // map language key to locale key
            languageToLocale: {
                'en-us': 'en-US'
            },
            selectedDateTime: null,
            selectedDate: null,
            selectedTime: '00:00:00',
            isSelectClicked: false,

            // report mode constant values
            reportModeMinuteInterval: 15
        };
    },
    created() {
        if (this.selectedDateTime) {
            this.selectedTime = this.selectedDateTime.split(' ')[1];
        }
    },
    watch: {
        modelValue: {
            // value is expected to be an ISO 8601 string
            immediate: true,
            handler(value) {
                if (value && value.length > 0) {
                    // here two values are expected. the first time user will load a
                    // page this function will receive the UTC value and from there on next time
                    // it will receive the iso formatted value which is pre-verified by the rules
                    // property on this component.
                    if (this.isValidUTC(value)) {
                        // convert iso string to interval format such as 2021/11/13 12:23:33
                        this.selectedDateTime = this.fromISOString(value);
                    } else {
                        // assign the already iso-formatted value to the data property.
                        this.selectedDateTime = value;
                    }
                    // set the selected date to the date picker
                    this.selectedDate = this.selectedDateTime;
                }
            }
        },
        selectedDateTime: {
            immediate: true,
            handler(value) {
                if (value && value.length === 19) {
                    try {
                        this.$emit(
                            'update:modelValue',
                            this.toISOString(value)
                        );
                    } catch (error) {
                        console.error(
                            'DateTimePicker watcher: Failed to convert datetime',
                            error
                        );
                    }
                }
            }
        },
        showPicker(isOpen) {
            // Auto-initialize with current date/time when picker opens
            if (
                isOpen &&
                this.defaultToNow &&
                (!this.selectedDateTime || this.selectedDateTime.length === 0)
            ) {
                const now = new Date();
                this.selectedDateTime = date.formatDate(now, this.pickerMask);
                this.selectedDate = this.selectedDateTime;
                this.selectedTime = this.selectedDateTime.split(' ')[1];
            }
        }
    },
    computed: {
        ...mapState(useAuthStore, {
            user: 'user',
            userLocale: 'getUserLocale'
        }),
        /**
         * Model mask to use for date & time.
         * @return {String} Mask
         */
        pickerMask() {
            return this.pickerMasks[this.userLocale];
        }
    },
    methods: {
        /**
         * Handle cancel.
         */
        cancel() {
            this.showPicker = false;
        },
        /**
         * Convert server format to picker format.
         * @param {string} serverDatetime - Datetime in server format.
         * @return {string} Datetime in picker format.
         */
        fromISOString(serverDatetime) {
            return date.formatDate(serverDatetime, this.pickerMask);
        },
        /**
         * Convert picker format to server format.
         * @param {string} pickerDatetime - Datetime in picker format (YYYY/MM/DD HH:MM:SS).
         * @return {string} Datetime in ISO8601 format.
         * @throws {Error} If datetime is invalid or empty.
         */
        toISOString(pickerDatetime) {
            // Input validation
            if (!pickerDatetime || typeof pickerDatetime !== 'string') {
                throw new Error(
                    'Invalid datetime input: must be non-empty string'
                );
            }

            const [datePart, timePart] = pickerDatetime.split(' ');

            // Validate date and time format using existing methods
            if (!this.isValidDate(datePart) || !this.isValidTime(timePart)) {
                throw new Error(
                    'Invalid datetime format: expected YYYY/MM/DD HH:MM:SS'
                );
            }

            // Use different variable name to avoid shadowing the imported 'date' utility
            const dateObj = new Date(pickerDatetime);

            // Check for invalid date (performance: only create Date after format validation)
            if (isNaN(dateObj.getTime())) {
                throw new Error('Invalid datetime: unable to parse date');
            }

            return dateObj.toISOString();
        },
        /**
         * Determine whether a given date should be selectable.
         * @param {String} date
         * @return {Boolean}
         */
        selectableDateRange(dateStr) {
            if (this.dateRange.start || this.dateRange.end) {
                const date = new Date(dateStr);
                const start = new Date(this.dateRange.start).setHours(
                    0,
                    0,
                    0,
                    0
                );
                const end = this.dateRange.end
                    ? new Date(this.dateRange.end).setHours(0, 0, 0, 0)
                    : new Date();
                return date >= start && date <= end;
            } else {
                // allow any date to be selected
                return true;
            }
        },
        selectedTimeChanged(value) {
            this.selectedTime = value;
        },
        select() {
            if (this.selectedDate) {
                const previousTime = this.selectedDate.split(' '); // 2021/08/24 12:00:00
                let timeToUse = this.selectedTime;

                // In report mode, analog clock doesn't include seconds, so append :00
                if (
                    this.reportMode &&
                    typeof timeToUse === 'string' &&
                    timeToUse.length > 0 &&
                    timeToUse.split(':').length === 2
                ) {
                    timeToUse += ':00';
                }

                this.selectedDateTime = previousTime[0] + ' ' + timeToUse;
            }

            try {
                this.$emit(
                    'update:modelValue',
                    this.toISOString(this.selectedDateTime)
                );
                this.showPicker = false;
            } catch (error) {
                console.error(
                    'DateTimePicker: Failed to convert datetime',
                    error
                );
            }
        },
        isValidDate(date) {
            if (date) {
                let dateRegEx = /^\d{4}([/])\d{2}\1\d{2}$/;
                if (date.match(dateRegEx)) {
                    return true;
                }
            }
            return false;
        },
        /**
         * Validate the selected time against the
         * specified format
         * @param {String} time - The time to validate
         * @return {Boolean} True if the time is valid, false otherwise
         */
        isValidTime(time) {
            if (time) {
                let timeRegEx =
                    /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
                if (time.match(timeRegEx)) {
                    return true;
                }
            }
            return false;
        },
        /**
         * Validate the selected date time against the
         * specified format
         * @param {String} string - The string to validate
         * @return {Boolean} True if the string matches the UTC format, false otherwise
         */
        isValidUTC(string) {
            if (!string) {
                return false;
            }
            const utcRegEx =
                /\b^(19|20)\d{2}-(0?[1-9]|1[012])-(0[1-9]|[12]\d|3[01])T[0-9]{2}:[0-9]{2}:[0-9]{2}.?\d*Z\b$/;
            return string.match(utcRegEx);
        },
        /**
         * Validate the selected date time against the
         * specified format
         * @return {Boolean} True if the selected date time is valid, false otherwise
         */
        isValidDateTime() {
            // Allow empty values if allowEmpty prop is true
            if (
                this.allowEmpty &&
                (!this.selectedDateTime || this.selectedDateTime.length === 0)
            ) {
                return true;
            }

            if (
                this.enableValidationRules &&
                typeof this.selectedDateTime === 'string' &&
                this.selectedDateTime.length !== 0 &&
                Date.parse(this.selectedDateTime)
            ) {
                let [date, time] = this.selectedDateTime.split(' ');
                if (this.isValidDate(date) && this.isValidTime(time)) {
                    if (this.reportMode) {
                        let reportTimeRegEx =
                            /^(0[0-9]|1[0-9]|2[0-3]):(00|15|30|45):00$/;
                        if (!time.match(reportTimeRegEx)) {
                            return this.$t(
                                'Report times must be in intervals of 15 minutes.'
                            );
                        }
                    }

                    this.selectedTime = time;
                    return true;
                } else {
                    return this.$t(
                        'Please enter valid date time format (YYYY/MM/DD HH:MM:SS).'
                    );
                }
            } else {
                return this.$t(
                    'Please enter valid date time format (YYYY/MM/DD HH:MM:SS).'
                );
            }
        },
        /**
         * Prevents invalid time input by blocking keypresses that would result in an invalid time.
         * @param {Event} event - The keydown event object.
         */
        preventInvalidTimeInput(event) {
            const { key, target } = event;
            const { selectionStart, value } = target;

            const allowedKeys = [
                'Backspace',
                'ArrowLeft',
                'ArrowRight',
                'Tab',
                'Delete',
                'Home',
                'End'
            ];
            if (allowedKeys.includes(key) || event.ctrlKey || event.metaKey) {
                return;
            }

            if (!/\d/.test(key)) {
                event.preventDefault();
                return;
            }

            const digit = parseInt(key, 10);
            let isInvalid = false;
            if (selectionStart === 11 && digit > 2) {
                isInvalid = true;
            } else if (
                selectionStart === 12 &&
                value[11] === '2' &&
                digit > 3
            ) {
                isInvalid = true;
            }
            // Validate Minutes
            else if (selectionStart === 14 && digit > 5) {
                isInvalid = true;
            }
            // Validate Seconds
            else if (selectionStart === 17 && digit > 5) {
                isInvalid = true;
            }

            if (isInvalid) {
                event.preventDefault();
            }
        },

        handleHideEvent() {
            if (this.selectedDateTime) {
                this.selectedDate = this.selectedDateTime;
                this.selectedTime = this.selectedDateTime.split(' ')[1];
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.datetime-picker-input {
    width: 100%;
}

:deep(.time-button > .q-btn__wrapper) {
    padding: 4px;
}

.datetime-picker-dialog {
    min-width: 350px;
}
</style>
